import {
  InputAdornment,
  makeStyles,
  Paper,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import ArchiveIcon from '@material-ui/icons/Archive';
import SearchIcon from '@material-ui/icons/Search';
import UnarchiveIcon from '@material-ui/icons/Unarchive';
import cn from 'classnames';
import { useMemo } from 'react';
import { useContext } from 'react';
import { Role } from '../../api/user/Role';
import { useGoalsContext } from '../../contexts/goals-context';
import { UserContext } from '../../contexts/user-context';
import { isMobile } from '../../utils/functions';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import { TestId } from '../Testing/TestId';
import { Text, TextField, GoalStatusSelect, Select } from '../common';
import { SelectGoalStatus } from '../common/goal-status-select';
import SelectVenturePopover from '../common/select-venture-popover';
import GoalsTableBasic from './goals-table-basic';

export interface GoalsFilters {
  venture?: { id: string; ventureName: string };
  status: SelectGoalStatus;
  search?: string;
}

export type GoalsFilterValue =
  | { id: string; ventureName: string }
  | SelectGoalStatus
  | string
  | undefined;

const useStyles = makeStyles((theme) => ({
  container: {
    padding: 0,
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 12px 12px 16px',
    height: 64,
    boxSizing: 'border-box',
  },
  filterList: {
    display: 'flex',
    alignItems: 'center',
  },
  filterItem: {
    '& + &': {
      marginLeft: 15,
    },
  },
  select: {
    minWidth: 160,
    width: '100%',
  },
  iconRemoveFilter: {
    width: 20,
  },
  containerRemoveFilter: {
    marginLeft: 10,
  },
  filterActions: {
    display: 'flex',
    alignItems: 'center',
    margin: '-1px 0',
  },
  filterActionsArchive: {
    marginLeft: 11,
  },
  filterActionsUnarchive: {
    marginLeft: 2,
  },
}));

function GoalsTable() {
  const classes = useStyles();
  const {
    goals,
    ventureOptions,
    filters,
    handleFilterUpdate,
    handlePageChange,
    handleGoalSliderChange,
    handleClearFilters,
    handleSearch,
    searchValue,
    isVenturesLoading,
    ventures,
    page,
    isLoading,
    loadError,
    hasNextPage,
    isNextPageLoading,
    showClearFilter,
    selectedGoalIds,
    setSelectedGoalIds,
    handleBulkArchive,
    handleBulkUnarchive,
    reloadGoals,
  } = useGoalsContext();

  const { hasRole, hasAccessToAction } = useContext(UserContext);

  const statusSelectedGoals = useMemo(() => {
    if (!selectedGoalIds.length) {
      return undefined;
    }
    const selectedGoals = goals.filter((goal) =>
      selectedGoalIds.includes(goal.id),
    );

    const activeSelectedGoals = selectedGoals.filter(
      (goal) => goal.status !== 'ARCHIVED',
    );

    const archivedSelectedGoals = selectedGoals.filter(
      (goal) => goal.status === 'ARCHIVED',
    );

    let disabledButton;
    if (activeSelectedGoals.length > 0 && archivedSelectedGoals.length === 0) {
      disabledButton = 'active';
    }
    if (archivedSelectedGoals.length > 0 && activeSelectedGoals.length === 0) {
      disabledButton = 'archived';
    }

    return {
      activeSelectedGoals,
      archivedSelectedGoals,
      disabledButton,
    };
  }, [selectedGoalIds, goals]);

  return (
    <Paper className={classes.container} data-testid='goals-table'>
      <div className={classes.filterContainer}>
        <div className={classes.filterList}>
          <div className={classes.filterItem}>
            <TextField
              className={classes.select}
              value={searchValue}
              onChange={handleSearch}
              placeholder='Search'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              small
            />
          </div>
          <div className={classes.filterItem}>
            <TestId testId={'goals-table-status-dropdown'}>
              <GoalStatusSelect
                status={filters.status}
                onChange={(status) => handleFilterUpdate('status', status)}
                className={classes.select}
                isDisabled={isVenturesLoading}
              />
            </TestId>
          </div>
          {!isMobile() && (
            <div className={classes.filterItem}>
              <TestId testId={'goals-table-venture-dropdown'}>
                {hasRole(Role.Admin) || hasRole(Role.Manager) ? (
                  <SelectVenturePopover
                    className={classes.select}
                    onChange={(val) =>
                      handleFilterUpdate('venture', val || undefined)
                    }
                    value={filters.venture}
                    isActive={!!filters.venture}
                    small
                  />
                ) : (
                  <Select
                    value={filters.venture ? filters.venture.id : 'ALL'}
                    options={ventureOptions}
                    onSelect={(venture) => {
                      if (venture && venture.value !== 'ALL') {
                        // Find venture from options to get name
                        const foundVenture = ventures.find(
                          (v) => v.id === venture.value,
                        );
                        if (foundVenture) {
                          handleFilterUpdate('venture', {
                            id: foundVenture.id,
                            ventureName: foundVenture.ventureName,
                          });
                        }
                      } else {
                        handleFilterUpdate('venture', undefined);
                      }
                    }}
                    isDisabled={isVenturesLoading}
                    className={classes.select}
                  />
                )}
              </TestId>
            </div>
          )}
          {showClearFilter && (
            <Tooltip title='Clear all filters'>
              <IconButton
                data-testid='button-remove-filter'
                className={classes.containerRemoveFilter}
                onClick={() => handleClearFilters()}>
                <img
                  src='/filter.svg'
                  alt='Remove filter'
                  className={classes.iconRemoveFilter}
                />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {selectedGoalIds.length > 0 &&
          hasAccessToAction('goal.checkins.create') && (
            <div className={classes.filterActions}>
              <Text variant='normal'>{selectedGoalIds.length} selected</Text>
              {hasAccessToAction('goal.checkins.create') && (
                <Tooltip title='Archive'>
                  <IconButton
                    className={cn(
                      classes.filterActionsArchive,
                      CLASS_TRACKING.INTERNAL_ACTION,
                    )}
                    onClick={() => handleBulkArchive(selectedGoalIds)}
                    disabled={
                      statusSelectedGoals?.disabledButton === 'archived'
                    }>
                    <ArchiveIcon />
                  </IconButton>
                </Tooltip>
              )}
              {hasAccessToAction('goal.checkins.create') && (
                <Tooltip title='Unarchive'>
                  <IconButton
                    className={cn(
                      classes.filterActionsUnarchive,
                      CLASS_TRACKING.INTERNAL_ACTION,
                    )}
                    onClick={() => handleBulkUnarchive(selectedGoalIds)}
                    disabled={statusSelectedGoals?.disabledButton === 'active'}>
                    <UnarchiveIcon />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          )}
      </div>
      <GoalsTableBasic
        goals={goals}
        page={page}
        hasNextPage={hasNextPage}
        isNextPageLoading={isNextPageLoading}
        isLoading={isLoading}
        hasError={loadError}
        venture={filters.venture}
        onPageChange={handlePageChange}
        onGoalSliderChange={handleGoalSliderChange}
        showVentures={[Role.Admin, Role.Manager, Role.Mentor].some(hasRole)}
        selectedGoalIds={selectedGoalIds}
        setSelectedGoalIds={setSelectedGoalIds}
        onGoalUpdate={async () => {
          await reloadGoals();
        }}
      />
    </Paper>
  );
}

export default GoalsTable;
